import HTTPClient, { HTTPClientParameters } from './HTTPClient';
import { PagedResponse } from 'types/Pagination';
import {
    ActiveCamera,
    AllCameraImage,
    CameraImageAt,
    CameraImageAtEventType,
    CarTracking,
    HistoricalOccupancy,
    RecentCameraImages,
    EnforcementStats,
    RentalStatsByType,
} from 'types/CarTracking';
import cache from './cache';

export const CAR_TRACKING_PAGE_SIZE = 20; // should be in sync with backend

type TrackingClientParams = HTTPClientParameters;

type CarTrackingFilters = {
    page: number;
    start: number;
    end: number;
    plate?: string;
    status?: string;
    isDemo?: boolean;
};

type ActiveCamerasOptions = {
    includeLatestImage?: boolean;
};

type CameraImageAtApiResponse = {
    image_url: string | null;
    thumbnail_url: string | null;
    captured_at: string | null;
    event_type: CameraImageAtEventType | null;
    offset: number;
    has_newer: boolean;
    has_older: boolean;
};

class TrackingClient extends HTTPClient {
    constructor(params: TrackingClientParams) {
        super(params);
        this.headers.set('Content-Type', 'application/json');
    }

    @cache(5 * 60000)
    getCarTrackingEvents(spotUuid: string, filters: CarTrackingFilters): Promise<PagedResponse<CarTracking>> {
        let path = `car_tracking?uuid=${spotUuid}&page=${filters.page}&end=${filters.end}&start=${filters.start}`;
        if (filters.plate) path += `&plate=${filters.plate}`;
        if (filters.status) path += `&status=${filters.status}`;
        if (filters.isDemo) path += `&is_demo=true`;

        return this.get(path);
    }

    getRecentCameraImages(spotUuid: string): Promise<RecentCameraImages> {
        return this.get(`camera/recent_images/${spotUuid}`);
    }

    getAllCameraImages(spotUuid: string, page: number, pageSize: number): Promise<AllCameraImage[]> {
        return this.get(`all_camera_images?uuid=${spotUuid}&page=${page}&page_size=${pageSize}`);
    }

    async getCameraImageAt(cameraId: number, spotUuid: string, offset = 0): Promise<CameraImageAt | null> {
        const response = await this.get<CameraImageAtApiResponse>(
            `cameras/${cameraId}/image_at/?spot=${encodeURIComponent(spotUuid)}&offset=${offset}`
        );
        if (!response.image_url || !response.captured_at || !response.event_type) {
            return null;
        }
        return {
            image_url: response.image_url,
            thumbnail_url: response.thumbnail_url || response.image_url,
            captured_at: response.captured_at,
            event_type: response.event_type,
            offset: response.offset,
            has_newer: response.has_newer,
            has_older: response.has_older,
        };
    }

    @cache(60000)
    getHistoricalOccupancy(spotUuid: string, avgSince?: number): Promise<HistoricalOccupancy> {
        let path = `historical_occupancy?uuid=${spotUuid}`;
        if (avgSince) path += `&avg_since=${avgSince}`;
        return this.get(path);
    }

    @cache(60000)
    getEnforcementStats(
        spotUuid: string,
        scansSince: number,
        granularity: 'day' | 'week' = 'day'
    ): Promise<EnforcementStats> {
        return this.get(`enforcement_stats?uuid=${spotUuid}&scans_since=${scansSince}&granularity=${granularity}`);
    }

    @cache(60000)
    getRentalStatsByType(spotUuid: string): Promise<RentalStatsByType> {
        return this.get(`rental_stats_by_type?uuid=${spotUuid}`);
    }

    @cache(60000)
    getActiveCameras(spotUuid: string, options: ActiveCamerasOptions = {}): Promise<ActiveCamera[]> {
        let path = `cameras/?spot=${spotUuid}`;
        if (options.includeLatestImage) {
            path += '&include_latest_image=true';
        }
        return this.get(path);
    }
}

export default TrackingClient;
